/**
 * App store: owns the engine, scanner loop, settings and derived UI state.
 * Hand-rolled useSyncExternalStore store — no state library needed at this
 * size (BRIEF §30: avoid unnecessary framework complexity).
 *
 * Mode wiring: the engine reads its deps through a getter, so flipping
 * settings.demoMode atomically switches adapters (Mock ↔ LiFi) and the
 * reserve provider (simulated balance ↔ on-chain USDC.e) for the NEXT
 * execution without disturbing one already in flight.
 */
import { erc20Abi } from "viem";
import { useSyncExternalStore } from "react";
import { MockBridge } from "./bridge/mock";
import { LifiBridge } from "./bridge/lifi";
import { MockSwap, demoStockPriceCents } from "./swap/mock";
import { LifiSwap } from "./swap/lifi";
import {
  allRoundups,
  allSpends,
  getCardTopUpState,
  getDemoReservePence,
  getGasBootstrapState,
  getSettings,
  saveSettings,
  setDemoReservePence,
  getScanState,
  resetDatabase,
} from "./db";
import { simulatePurchase, simulateBriefExample } from "./demo";
import { RoundupEngine, makeDemoReserve, type EngineDeps, type EngineEvent } from "./pipeline/engine";
import { getUsdPerCurrencyE6, getStockPriceCents } from "./prices";
import { scanForCardSpends } from "./gnosis/scanner";
import {
  beginCardTopUp as startCardTopUp,
  resumeCardTopUp as continueCardTopUp,
} from "./card-topup";
import { importWalletKey, loadOrCreateWallet, type LocalWallet } from "./wallet";
import { gnosisPublicClient } from "./chains";
import { prepareDestinationGas, destEthBalance } from "./gas";
import { maintainGasBootstrap as runGasBootstrap } from "./gas-sponsor";
import { MIN_VIABLE_BRIDGE_PENCE, RESERVE_TOKEN, SCAN_INTERVAL_MS } from "./config";
import type {
  AppSettings,
  CardTopUpState,
  DetectedSpend,
  FiatCurrency,
  GasBootstrapState,
  Hex,
  RoundupInvestment,
} from "./types";

export interface CelebrationPayload {
  stockSymbol: string;
  investedPence: number;
  roundupPence: number;
  costPence: number;
  count: number;
  currency: FiatCurrency;
}

export interface LiveBalances {
  /** Reserve token (USDC.e) units on Gnosis, 6dp. */
  reserveUnits: bigint;
  /** xDAI wei on Gnosis (pays approve/bridge gas). */
  xdaiWei: bigint;
  /** ETH wei on Robinhood Chain (pays swap gas; refilled from batches). */
  destEthWei: bigint;
  fetchedAt: number;
}

export interface AppState {
  ready: boolean;
  settings: AppSettings;
  roundups: RoundupInvestment[];
  spends: DetectedSpend[];
  reservePence: number;
  walletAddress?: Hex;
  liveBalances?: LiveBalances;
  gasBootstrap?: GasBootstrapState;
  cardTopUp?: CardTopUpState;
  lastScannedBlock?: number;
  scanning: boolean;
  rpcOk: boolean;
  celebration?: CelebrationPayload;
  executing: boolean;
  errors: string[];
}

const state: AppState = {
  ready: false,
  settings: {
    onboarded: false,
    stockSymbol: "NVDA",
    incrementPence: 100,
    demoMode: true,
    currency: "GBP",
  },
  roundups: [],
  spends: [],
  reservePence: 0,
  scanning: false,
  rpcOk: true,
  executing: false,
  errors: [],
};

const listeners = new Set<() => void>();
let snapshot = { ...state };

function emit() {
  snapshot = { ...state };
  listeners.forEach((l) => l());
}

export function useAppState(): AppState {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => snapshot,
  );
}

// ---------- engine wiring ----------

function onEngineEvent(e: EngineEvent) {
  if (e.type === "invested") {
    state.celebration = {
      stockSymbol: e.stockSymbol,
      investedPence: e.investedPence,
      roundupPence: e.roundupPence,
      costPence: e.costPence,
      count: e.count,
      currency: e.currency,
    };
  } else if (e.type === "executing") {
    state.executing = true;
  } else if (e.type === "error") {
    state.errors = [...state.errors.slice(-4), e.message];
  }
  emit();
}

let wallet: LocalWallet | null = null;
const demoBridge = new MockBridge();
const demoSwap = new MockSwap();
let liveBridge: LifiBridge | null = null;
let liveSwap: LifiSwap | null = null;
const demoReserve = makeDemoReserve();

/** Live reserve = on-chain USDC.e expressed in the selected fiat minor unit. */
async function liveReservePence(): Promise<number> {
  return liveReserveMinor(state.settings.currency);
}

/** Live USD reserve expressed in the requested card currency's minor units. */
async function liveReserveMinor(currency: FiatCurrency): Promise<number> {
  if (!wallet) return 0;
  const units = await gnosisPublicClient().readContract({
    address: RESERVE_TOKEN.address,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: [wallet.address],
  });
  const usdCents = Number((units * 100n) / 10n ** BigInt(RESERVE_TOKEN.decimals));
  const rateE6 = await getUsdPerCurrencyE6(currency);
  return Math.floor((usdCents * 1_000_000) / rateE6);
}

function currentDeps(): EngineDeps {
  const demo = state.settings.demoMode;
  if (demo || !wallet) {
    return {
      bridge: demoBridge,
      swap: demoSwap,
      getUsdPerCurrencyE6,
      getMinViablePence: () => 25,
      ...demoReserve,
      onEvent: onEngineEvent,
    };
  }
  liveBridge ??= new LifiBridge(wallet);
  liveSwap ??= new LifiSwap(wallet);
  const w = wallet;
  return {
    bridge: liveBridge,
    swap: liveSwap,
    getUsdPerCurrencyE6,
    getMinViablePence: () => MIN_VIABLE_BRIDGE_PENCE,
    getReservePence: liveReserveMinor,
    // Real funds move via the actual transactions; the invariant
    // `debit == Σ round-ups` is enforced by sizing the txs from the batch.
    debitReservePence: async () => {},
    creditReservePence: async () => {},
    prepareDestinationGas: (batchUnits) => prepareDestinationGas(w, batchUnits),
    onEvent: onEngineEvent,
  };
}

const engine = new RoundupEngine(currentDeps);

async function refreshFromDb() {
  const settings = await getSettings();
  const [roundups, spends, scan, gasBootstrap, cardTopUp] = await Promise.all([
    allRoundups(),
    allSpends(),
    settings.safeAddress ? getScanState(settings.safeAddress) : Promise.resolve(undefined),
    wallet ? getGasBootstrapState(wallet.address) : Promise.resolve(undefined),
    wallet ? getCardTopUpState(wallet.address) : Promise.resolve(undefined),
  ]);
  state.settings = settings;
  state.roundups = roundups;
  state.spends = spends;
  state.lastScannedBlock = scan?.lastScannedBlock;
  state.gasBootstrap = gasBootstrap;
  state.cardTopUp = cardTopUp;
  try {
    state.reservePence = settings.demoMode ? await getDemoReservePence() : await liveReservePence();
  } catch {
    // RPC hiccup — keep the previous value rather than flashing £0.
  }
  emit();
}

export async function refreshLiveBalances(): Promise<void> {
  if (!wallet || state.settings.demoMode) return;
  try {
    const client = gnosisPublicClient();
    const [reserveUnits, xdaiWei, destEthWei] = await Promise.all([
      client.readContract({
        address: RESERVE_TOKEN.address,
        abi: erc20Abi,
        functionName: "balanceOf",
        args: [wallet.address],
      }),
      client.getBalance({ address: wallet.address }),
      destEthBalance(wallet),
    ]);
    state.liveBalances = { reserveUnits, xdaiWei, destEthWei, fetchedAt: Date.now() };
    emit();
  } catch {
    // non-fatal; Settings shows last-known values
  }
}

// ---------- public actions ----------

export async function initApp(): Promise<void> {
  wallet = await loadOrCreateWallet();
  state.walletAddress = wallet.address;
  await engine.recoverUnfinished();
  await refreshFromDb();
  state.ready = true;
  emit();
  // Process anything that became eligible while we were away (BRIEF §27),
  // including resuming batches interrupted mid-bridge or mid-swap.
  await engine.executeEligible();
  await refreshFromDb();
  state.executing = false;
  emit();
  void refreshLiveBalances();
  startScanLoop();
  void maintainGasAccount();
  void resumeLiveCardTopUp();
}

let scanTimer: ReturnType<typeof setInterval> | null = null;

function startScanLoop() {
  if (scanTimer) return;
  scanTimer = setInterval(scanTick, SCAN_INTERVAL_MS);
  // Re-scan promptly when the PWA returns from background (BRIEF §30).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void scanTick();
      void maintainGasAccount();
      void resumeLiveCardTopUp();
    }
  });
  void scanTick();
}

let scanBusy = false;

async function scanTick() {
  const s = state.settings;
  if (!s.onboarded || s.demoMode || !s.safeAddress || scanBusy) return;
  scanBusy = true;
  state.scanning = true;
  emit();
  try {
    // Spends are processed inside the scan, before each chunk's watermark is
    // saved — a crash mid-scan re-scans idempotently instead of losing them.
    const result = await scanForCardSpends(s.safeAddress, (spend) => engine.processSpend(spend));
    state.rpcOk = true;
    const detectedCurrency = result.spends[0]?.currency;
    if (detectedCurrency && detectedCurrency !== state.settings.currency) {
      const next = { ...state.settings, currency: detectedCurrency };
      await saveSettings(next);
      state.settings = next;
    }
    if (result.spends.length > 0) await engine.executeEligible();
    state.lastScannedBlock = result.scannedTo;
  } catch {
    state.rpcOk = false;
  } finally {
    scanBusy = false;
    state.scanning = false;
    state.executing = false;
    await refreshFromDb();
  }
}

export async function updateSettings(patch: Partial<AppSettings>): Promise<void> {
  const next = { ...state.settings, ...patch };
  await saveSettings(next);
  state.settings = next;
  emit();
  if (!next.demoMode) void refreshLiveBalances();
}

let gasMaintenance: Promise<void> | null = null;

/**
 * Request/resume the one-time gas advance and repay it from the first USDC.e
 * deposit. Multiple UI/background triggers collapse into one execution.
 */
export function maintainGasAccount(onProgress?: (label: string) => void): Promise<void> {
  if (gasMaintenance) return gasMaintenance;
  if (
    !wallet ||
    !state.settings.onboarded ||
    state.settings.demoMode ||
    !state.settings.safeAddress
  ) {
    return Promise.resolve();
  }
  gasMaintenance = runGasBootstrap(wallet, state.settings.safeAddress, onProgress)
    .then(async (gasBootstrap) => {
      state.gasBootstrap = gasBootstrap;
      emit();
      await refreshFromDb();
    })
    .catch(async (error) => {
      state.gasBootstrap = await getGasBootstrapState(wallet!.address);
      state.errors = [
        ...state.errors.slice(-4),
        `Gas setup: ${String(error instanceof Error ? error.message : error)}`,
      ];
      emit();
    })
    .finally(() => {
      gasMaintenance = null;
    });
  return gasMaintenance;
}

let cardTopUpMaintenance: Promise<void> | null = null;

export async function beginLiveCardTopUp(): Promise<void> {
  if (!wallet || state.settings.demoMode || !state.settings.safeAddress) {
    throw new Error("A live Gnosis Pay card is required");
  }
  state.cardTopUp = await startCardTopUp(wallet, state.settings.safeAddress);
  emit();
}

export function resumeLiveCardTopUp(onProgress?: (label: string) => void): Promise<void> {
  if (cardTopUpMaintenance) return cardTopUpMaintenance;
  if (!wallet || state.settings.demoMode || !state.cardTopUp || state.cardTopUp.status === "complete") {
    return Promise.resolve();
  }
  cardTopUpMaintenance = continueCardTopUp(wallet, onProgress)
    .then(async (cardTopUp) => {
      state.cardTopUp = cardTopUp;
      emit();
      await refreshLiveBalances();
      await refreshFromDb();
    })
    .catch(async (error) => {
      state.cardTopUp = await getCardTopUpState(wallet!.address);
      state.errors = [
        ...state.errors.slice(-4),
        `Card top-up: ${String(error instanceof Error ? error.message : error)}`,
      ];
      emit();
    })
    .finally(() => {
      cardTopUpMaintenance = null;
    });
  return cardTopUpMaintenance;
}

export async function topUpReserve(pence: number): Promise<void> {
  if (!state.settings.demoMode) return; // live top-ups are on-chain transfers
  const cur = await getDemoReservePence();
  await setDemoReservePence(cur + pence);
  await refreshFromDb();
  await engine.executeEligible();
  state.executing = false;
  await refreshFromDb();
}

/** Demo action: simulate a card purchase and run the full loop. */
export async function demoPurchase(brief = false): Promise<void> {
  const spend = brief ? simulateBriefExample() : simulatePurchase();
  await engine.processSpend(spend);
  await refreshFromDb();
  await engine.executeEligible();
  state.executing = false;
  await refreshFromDb();
}

export async function retryRoundup(id: string): Promise<void> {
  await engine.retry(id);
  await engine.executeEligible();
  state.executing = false;
  await refreshFromDb();
}

export function dismissCelebration(): void {
  state.celebration = undefined;
  emit();
}

export function exportWalletKey(): Hex | null {
  return wallet ? wallet.exportPrivateKey() : null;
}

/** Restore the wallet from another device's exported key (onboarding). */
export async function importWallet(pk: string): Promise<LocalWallet> {
  const w = await importWalletKey(pk);
  wallet = w;
  // Live adapters are bound to the old wallet — rebuild on next use.
  liveBridge = null;
  liveSwap = null;
  state.walletAddress = w.address;
  emit();
  return w;
}

export async function resetApp(): Promise<void> {
  if (scanTimer) {
    clearInterval(scanTimer);
    scanTimer = null;
  }
  await resetDatabase();
  window.location.reload();
}

// ---------- derived helpers ----------

export function portfolioTotals(
  roundups: RoundupInvestment[],
  stockSymbol: string,
  currency: FiatCurrency = "GBP",
) {
  const belongsToPortfolio = (r: RoundupInvestment) =>
    r.stockSymbol === stockSymbol && r.currency === currency;
  const complete = roundups.filter((r) => r.status === "complete" && belongsToPortfolio(r));
  const investedPence = complete.reduce((s, r) => s + (r.investedPence ?? 0), 0);
  const stockUnits = complete.reduce((s, r) => s + BigInt(r.stockAmountUnits ?? "0"), 0n);
  const count = complete.length;
  const pendingPence = roundups
    .filter((r) => r.status === "queued" && belongsToPortfolio(r))
    .reduce((s, r) => s + r.roundupPence, 0);
  return { investedPence, stockUnits, count, pendingPence };
}

/** Current value of holdings in pence: live price when available, demo fallback. */
export async function currentValuePence(
  stockUnits: bigint,
  stockSymbol: string,
  currency: FiatCurrency = "GBP",
): Promise<number> {
  const priceCents = BigInt(
    await getStockPriceCents(stockSymbol, !state.settings.demoMode).catch(() =>
      demoStockPriceCents(stockSymbol),
    ),
  );
  const usdCents = Number((stockUnits * priceCents) / 10n ** 18n);
  const rateE6 = await getUsdPerCurrencyE6(currency);
  return Math.floor((usdCents * 1_000_000) / rateE6);
}
